import { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { $api } from "@/api/api";
import { paths } from "@/api/v1";
import {
  canAccessPermissionCategory,
  type PermissionCategory,
} from "@/utils/permissions";
import { MINIMAL_REFETCH } from "@/utils/query-settings/queryOptions";

import { ApiTokenContext } from "../api-token/ApiContextWrapper";


type LoggedInUser =
  paths["/login"]["get"]["responses"]["200"]["content"]["application/json"];

const UserContext = createContext<{
  user: LoggedInUser | null;
}>({
  user: null,
});
function UserProvider({ children }: { children: ReactNode }) {
  const { token } = useContext(ApiTokenContext),
    [user, setUser] = useState<LoggedInUser | null>(null),
    hasBearerToken = typeof token === "string" && token.length > 0,
    { data } = $api.useQuery(
      "get",
      "/login",
      { headers: { Authorization: `Bearer ${token}` } },
      { enabled: hasBearerToken, ...MINIMAL_REFETCH },
    );

  useEffect(() => {
    if (!hasBearerToken) {
      setUser(null);
      return;
    }

    if (data) {
      setUser(data);
    }
  }, [hasBearerToken, data]);

  return (
    <UserContext.Provider value={{ user }}>{children}</UserContext.Provider>
  );
}

function useUser(): LoggedInUser {
  const { user } = useContext(UserContext);
  if (!user) {
    throw new Error("User not found");
  }
  return user;
}

function hasPermission(user: LoggedInUser, permission: string): boolean {
  const normalizedPermission = permission.toUpperCase();

  return user.PermissionsData.some(
    (role) => role.toUpperCase() === normalizedPermission,
  );
}

function hasAnyPermission(user: LoggedInUser, permissions: string[]): boolean {
  return permissions.some((permission) =>
    user.PermissionsData.includes(permission),
  );
}

function useHasPermission(permission: string): boolean {
  const user = useUser();
  return hasPermission(user, permission);
}

function usePermission(category: PermissionCategory): boolean {
  const user = useUser();
  return canAccessPermissionCategory(user.PermissionsData, category);
}

export {
  hasAnyPermission,
  hasPermission,
  useHasPermission,
  usePermission,
  UserContext,
  UserProvider,
  useUser,
  type LoggedInUser,
};
